/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useRef } from 'react';

import type { AriaSelectProps } from '@react-types/select';
import { AnimatePresence, motion } from 'framer-motion';
import i18n from 'i18n';
import Cookies from 'js-cookie';
import { mergeProps, useButton, useFocusRing, useSelect } from 'react-aria';
import { useTranslation } from 'react-i18next';
import { Item, useSelectState } from 'react-stately';
import { styled } from 'styled-components';

import ListBox from './ListBox';
import Popover from './Popover';

import { ReactComponent as IconGlobe } from './icon-globe.svg';

const capitalize = (str: string): string => {
  if (!str) {
    return '';
  }

  return str.charAt(0).toUpperCase() + str.slice(1);
};

const getLanguageFullName = (code: string): string => {
  const languageNames: Record<string, string> = {
    ar: 'العربية',
    de: 'Deutsche',
    en: 'English',
    es: 'Español',
    fa: 'فارسی',
    fr: 'Français',
    ja: '日本語',
    kk: 'Қазақша',
    ko: '한국어',
    pa: 'ਪੰਜਾਬੀ',
    pl: 'Polski',
    pt: 'Português',
    ru: 'Русский',
    tr: 'Türkçe',
    uk: 'Українська',
    uz: "O'zbekcha",
    zh: '中文',
  };

  const languageName = languageNames[code];

  return capitalize(languageName || '');
};

const languages = Object.keys(i18n.services.resourceStore.data).map((lang) => ({
  name: lang,
}));

const Image = styled.img`
  width: 24px;
  height: 24px;
`;

const Text = styled.span`
  color: var(--primary);
`;

const Button = styled.button`
  border: none;
  background-color: var(--secondary);
  border-radius: 999px;
  width: 40px;
  height: 40px;
  cursor: pointer;
  color: var(--primary);

  &.small {
    width: 26px;
    height: 26px;
  }
`;

const Select = <T extends object>(
  props: AriaSelectProps<T> & {
    backgroundColor?: string;
    className?: string;
    color?: string;
    type?: 'widget';
  },
) => {
  // @ts-ignore
  const state = useSelectState(props);

  const ref = useRef<HTMLButtonElement>(null);

  //@ts-ignore
  //@ts-nocheck
  const { triggerProps, menuProps } = useSelect(
    // @ts-ignore
    { ...props, ['aria-label']: 'language selector' },
    state,
    ref,
  );

  const { buttonProps } = useButton(triggerProps, ref);
  const { focusProps } = useFocusRing();

  return (
    <div>
      <Button
        {...mergeProps(buttonProps, focusProps)}
        ref={ref}
        className={props.className}
        style={{
          alignItems: 'center',
          background: props?.backgroundColor,
          color: props?.color,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <IconGlobe />
      </Button>

      <AnimatePresence>
        {state.isOpen && (
          <Popover offset={8} placement='bottom end' state={state} triggerRef={ref}>
            <motion.div
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <ListBox {...menuProps} isWidget={props?.type === 'widget'} state={state} />
            </motion.div>
          </Popover>
        )}
      </AnimatePresence>
    </div>
  );
};

interface ILanguageSelect {
  backgroundColor?: string;
  color?: string;
  size?: 'default' | 'small';
  type?: 'widget';
}

const LanguageSelect = ({ color, backgroundColor, size, type }: ILanguageSelect) => {
  const { i18n } = useTranslation();

  return (
    <Select
      backgroundColor={backgroundColor}
      className={size === 'small' ? 'small' : undefined}
      color={color}
      items={languages}
      selectedKey={i18n.language}
      type={type}
      onSelectionChange={(lang) => {
        i18n.changeLanguage(lang as string);
        //!!!!ACHTUNG!!!
        //Это ГЛОБАЛЬНАЯ смена языка, она унифицирована в каждом приложении
        //!!!Если меняешь здесь - поменяй во всех сервисах тоже!!!

        const expirationDate = new Date();

        const domain_name = 'cryptomus.com';

        expirationDate.setFullYear(expirationDate.getFullYear() + 10);

        if (globalThis.location.host.includes(domain_name)) {
          Cookies.set('i18next', `${lang}`, {
            domain: domain_name,
            expires: expirationDate,
          });
        } else {
          Cookies.set('i18next', `${lang}`, { expires: expirationDate });
        }
      }}
    >
      {({ name: lang }) => (
        <Item key={lang} textValue={lang}>
          <Image alt={lang} src={`/flags/flag-${lang}.svg`} />
          <Text>{getLanguageFullName(lang)}</Text>
        </Item>
      )}
    </Select>
  );
};

export default LanguageSelect;
